import React, { useState, useEffect } from 'react';
import { 
  CheckCircle,
  Calendar,
  FileText,
  Clock,
  Award,
  AlertCircle,
  Loader,
  CreditCard
} from 'lucide-react';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';

const Dashboard = () => {
  return (
    <div className="dashboard-container">
      <Header />
      <div className="dashboard-wrapper">
        <Sidebar activeItem="dashboard" />
        <div className="flex-grow-1 dashboard-content">
          <DashboardOverview />
        </div>
      </div>
    </div>
  );
};

const DashboardOverview = () => {
  const [swimmingRecords, setSwimmingRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSwimmingRecords();
  }, []);

  const fetchSwimmingRecords = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        setLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}api/get-swimming-records`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        setSwimmingRecords(data.data);
      } else {
        setError('Failed to fetch records');
      }
    } catch (err) {
      setError('Error fetching swimming records');
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
        <Loader className="animate-spin" size={32} />
        <span className="ms-2">Loading swimming records...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <AlertCircle className="me-2" size={20} />
        {error}
      </div>
    );
  }

  const latestRecord = swimmingRecords[0];

  return (
    <div>
      <h2 className="dashboard-overview-title">Dashboard Overview</h2>
      
      {/* Stats Cards */}
      <div className="row mb-4">
        <div className="col-lg-3 col-md-6 mb-3">
          <div className="stat-card success h-100">
            <div className="card-body d-flex flex-column justify-content-between">
              <div>
                <div className="icon-wrapper">
                  <CheckCircle size={32} />
                </div>
                <h6 className="text-muted mb-2">Application Status</h6>
              </div>
              <h4 className="text-success mb-0">
                {latestRecord?.status === 1 ? 'Approved' : 'Pending'}
              </h4>
            </div>
          </div>
        </div>
        
        <div className="col-lg-3 col-md-6 mb-3">
          <div className="stat-card primary h-100">
            <div className="card-body d-flex flex-column justify-content-between">
              <div>
                <div className="icon-wrapper">
                  <Calendar size={32} />
                </div>
                <h6 className="text-muted mb-2">Current Session</h6>
              </div>
              <h4 className="text-primary mb-0">
                {latestRecord?.session?.session || 'N/A'}
              </h4>
            </div>
          </div>
        </div>
        
        <div className="col-lg-3 col-md-6 mb-3">
          <div className="stat-card purple h-100">
            <div className="card-body d-flex flex-column justify-content-between">
              <div>
                <div className="icon-wrapper">
                  <Award size={32} />
                </div>
                <h6 className="text-muted mb-2">Category</h6>
              </div>
              <h4 className="text-purple mb-0">
                {latestRecord?.category?.category || 'N/A'}
              </h4>
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-md-6 mb-3">
          <div className="stat-card info h-100">
            <div className="card-body d-flex flex-column justify-content-between">
              <div>
                <div className="icon-wrapper">
                  <Clock size={32} />
                </div>
                <h6 className="text-muted mb-2">Time Slot</h6>
              </div>
              <h4 className="text-info mb-0">
                {latestRecord?.time_slot?.time || 'N/A'}
              </h4>
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-md-6 mb-3">
          <div className={`stat-card ${latestRecord?.payment_status === '1' ? 'success' : 'warning'} h-100`}>
            <div className="card-body d-flex flex-column justify-content-between">
              <div>
                <div className="icon-wrapper">
                  <CreditCard size={32} />
                </div>
                <h6 className="text-muted mb-2">Payment Status</h6>
              </div>
              <h4 className={`text-${latestRecord?.payment_status === '1' ? 'success' : 'warning'} mb-0`}>
                {latestRecord?.payment_status === '1' ? 'Paid' : 'Pending'}
              </h4>
            </div>
          </div>
        </div>
      </div>

      
      {/* Recent Activity */}
      <div className="row">
        <div className="col-lg-6 mb-4">
          <div className="activity-card">
            <h3 className="h5 text-dark mb-4">Recent Activity</h3>
            {latestRecord && (
              <>
                <div className="activity-item success">
                  <div className="d-flex align-items-center">
                    <CheckCircle className="text-success me-3" size={20} />
                    <div>
                      <p className="mb-0 fw-medium">Registration Completed</p>
                      <p className="small text-muted mb-0">
                        {new Date(latestRecord.registration_date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="activity-item primary">
                  <div className="d-flex align-items-center">
                    <FileText className="text-primary me-3" size={20} />
                    <div>
                      <p className="mb-0 fw-medium">Documents Uploaded</p>
                      <p className="small text-muted mb-0">
                        {new Date(latestRecord.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="activity-item purple">
                  <div className="d-flex align-items-center">
                    <Calendar className="text-purple me-3" size={20} />
                    <div>
                      <p className="mb-0 fw-medium">Session: {latestRecord.session?.session}</p>
                      <p className="small text-muted mb-0">Current active session</p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
